import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Tr, TypeUtils } from '@utils';
import { Organization, Permission } from '@services';
import { Divider, Grid, Stack } from '@mui/material';
import { FormikSwitch, FormikTextfield } from '@components/common';
import { FormStepLayout, FormStepQuestion } from '@components/portal/common';

type EntityType = Organization;
type FormFields = Partial<Organization>;

const getInitialValues = (entity?: Partial<EntityType>) => ({
	name: entity?.name || '',
	domain: entity?.domain || '',
	address: entity?.address || '',
	vat: entity?.vat || '',
	permissions: entity?.permissions || [],
	isInfraSeller: entity?.isInfraSeller || false,
	isVehicleSeller: entity?.isVehicleSeller || false,
	isB2CSeller: entity?.isB2CSeller || false,
	fastLogin: entity?.fastLogin || false
});

export interface IAddEditOrganizationFormProps {
	values?: Partial<EntityType>;
	onSubmit?: (values: Partial<EntityType>) => void;
}

export const AddEditOrganizationForm: FC<IAddEditOrganizationFormProps> = ({ values, onSubmit }) => {
	const { t } = useTranslation('portal');

	const formik = useFormik<FormFields>({
		initialValues: getInitialValues(values),
		enableReinitialize: true,
		validateOnChange: true,
		validateOnMount: true,
		validationSchema: yup.object({
			name: yup.string().required(t('field-required')),
			domain: yup.string().required(t('field-required')),
			address: yup.string().required(t('field-required')),
			vat: yup.string().required(t('field-required')),
			latitude: yup.number().optional(),
			longitude: yup.number().optional(),
			isInfraSeller: yup.boolean().optional(),
			isVehicleSeller: yup.boolean().optional(),
			isB2CSeller: yup.boolean().optional(),
			fastLogin: yup.boolean().optional()
		}),
		onSubmit: (
			{ name, domain, address, vat, permissions: _permissions, isInfraSeller, isVehicleSeller, isB2CSeller, fastLogin },
			api
		) => {
			api.setSubmitting(false);
			onSubmit?.(
				TypeUtils.convert(Organization, {
					...values,
					name,
					domain,
					address,
					vat,
					permissions: Object.values(Permission),
					isInfraSeller,
					isVehicleSeller,
					isB2CSeller,
					fastLogin
				})
			);
		}
	});
	const { submitForm, handleSubmit } = formik;
	const disabled = !formik.isValid || formik.isSubmitting;

	return (
		<form onSubmit={handleSubmit} style={{ flexGrow: 1, overflowY: 'hidden' }}>
			<FormStepLayout
				primaryButtonProps={{
					disabled,
					children: (
						<Tr.Portal path={values ? 'organizations.add-edit-form.update-button' : 'organizations.add-edit-form.add-button'} />
					),
					onClick: submitForm
				}}
				hideSecondaryButton
			>
				<Stack direction="column" spacing={4}>
					<FormStepQuestion title={<Tr.Portal path="organizations.assign-organization-detail" />}>
						<Grid container rowSpacing={2} columnSpacing={3}>
							<Grid item xs={6}>
								<FormikTextfield
									formikApi={formik}
									name="name"
									title={t('organizations.organization-name')}
									placeholder={t('organizations.organization-name')}
									fullWidth
									required
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikTextfield
									formikApi={formik}
									name="vat"
									title={t('organizations.vat-code')}
									placeholder={t('organizations.vat-code')}
									fullWidth
									required
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikTextfield
									formikApi={formik}
									name="domain"
									title={t('organizations.domain-name')}
									placeholder={t('organizations.domain-name')}
									fullWidth
									required
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikTextfield
									formikApi={formik}
									name="address"
									title={t('organizations.address')}
									placeholder={t('organizations.address')}
									fullWidth
									required
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>
					<Divider light />

					<FormStepQuestion title={<Tr.Portal path="organizations.select-product-type" />}>
						<Grid container rowSpacing={2} columnSpacing={3}>
							<Grid item xs={6}>
								<FormikSwitch
									formikApi={formik}
									name="isInfraSeller"
									title={t('organizations.product-infrastructure')}
									direction="row"
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikSwitch
									formikApi={formik}
									name="isVehicleSeller"
									title={t('organizations.product-vehicle')}
									direction="row"
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>
					<Divider light />

					<FormStepQuestion title={<Tr.Portal path="organizations.select-business-type" />}>
						<Grid container rowSpacing={2} columnSpacing={3}>
							<Grid item xs={6}>
								<FormikSwitch
									formikApi={formik}
									name="isB2CSeller"
									title={t('organizations.business-b2c')}
									direction="row"
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>
					<Divider light />

					<FormStepQuestion title={<Tr.Portal path="organizations.select-fastlogin" />}>
						<Grid container rowSpacing={2} columnSpacing={3}>
							<Grid item xs={6}>
								<FormikSwitch formikApi={formik} name="fastLogin" title={t('organizations.fastlogin')} direction="row" />
							</Grid>
						</Grid>
					</FormStepQuestion>
				</Stack>
			</FormStepLayout>
		</form>
	);
};
